import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useState, useEffect } from 'react';

const languages = [
  { code: 'pt', label: 'Português' },
  { code: 'de', label: 'Deutsch' },
  { code: 'en', label: 'English' },
];

const categories = [
  { key: 'cat.shoes', url: 'https://kaspreadsheet.com/category-911-Shoes.html' },
  { key: 'cat.hoodies', url: 'https://kaspreadsheet.com/category-912-Hoodies%2FSweaters.html' },
  { key: 'cat.tshirts', url: 'https://kaspreadsheet.com/category-913-T-Shirts.html' },
  { key: 'cat.jackets', url: 'https://kaspreadsheet.com/category-914-Jackets.html' },
  { key: 'cat.pants', url: 'https://kaspreadsheet.com/category-915-Pants%2FShorts.html' },
  { key: 'cat.headwear', url: 'https://kaspreadsheet.com/category-916-Headwear.html' },
  { key: 'cat.sets', url: 'https://kaspreadsheet.com/category-917-Sets.html' },
  { key: 'cat.underwear', url: 'https://kaspreadsheet.com/category-918-underwear%2Funderpants.html' },
  { key: 'cat.jersey', url: 'https://kaspreadsheet.com/category-919-Jersey.html' },
  { key: 'cat.accessories', url: 'https://kaspreadsheet.com/category-920-Accessories.html' },
  { key: 'cat.bags', url: 'https://kaspreadsheet.com/category-921-Bags.html' },
  { key: 'cat.belts', url: 'https://kaspreadsheet.com/category-922-Belts.html' },
  { key: 'cat.perfumes', url: 'https://kaspreadsheet.com/category-923-Perfumes.html' },
  { key: 'cat.other', url: 'https://kaspreadsheet.com/category-924-Other+Stuff.html' },
];

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [catOpen, setCatOpen] = useState(false);
  const [langOpen, setLangOpen] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileCatOpen, setMobileCatOpen] = useState(false);

  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const currentLang = languages.find((l) => l.code === i18n.language) || languages[0];

  const getPath = (base: string) => {
    const lang = i18n.language === 'pt' ? '' : `/${i18n.language}`;
    return `${lang}${base}`;
  };

  const getProductsPath = () => {
    if (i18n.language === 'pt') return '/produtos/';
    if (i18n.language === 'de') return '/produkte/';
    return '/products/';
  };

  const guidePath =
    i18n.language === 'pt' ? '/cssbuy-spreadsheet-guia/' :
    i18n.language === 'de' ? '/cssbuy-spreadsheet-anleitung/' : '/cssbuy-spreadsheet-guide/';

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 bg-white/95 backdrop-blur-md ${
      scrolled ? 'border-b border-background-200 shadow-sm' : 'border-b border-background-200/60'
    }`}>
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-[72px]">
          <Link to={getPath('/')} className="text-lg md:text-xl font-bold tracking-tight text-foreground-950 whitespace-nowrap">
            CSSBuy <span className="text-primary-600">Spreadsheet</span>
          </Link>

          <div className="hidden lg:flex items-center gap-6">
            <Link to={getPath('/')} className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.home')}
            </Link>

            <Link to={getProductsPath()} className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.products')}
            </Link>

            <Link to={guidePath} className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.shoppingGuide')}
            </Link>

            <div className="relative">
              <button
                onClick={() => setCatOpen(!catOpen)}
                className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors flex items-center gap-1 whitespace-nowrap"
              >
                {t('nav.categories')} <i className={`ri-arrow-down-s-line transition-transform ${catOpen ? 'rotate-180' : ''}`} />
              </button>
              {catOpen && (
                <div className="absolute top-full mt-2 left-1/2 -translate-x-1/2 w-64 bg-white rounded-xl border border-background-200 py-2 max-h-96 overflow-y-auto shadow-lg">
                  {categories.map((c) => (
                    <a
                      key={c.key}
                      href={c.url}
                      target="_blank"
                      rel="noopener nofollow"
                      className="block px-4 py-2.5 text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                      onClick={() => setCatOpen(false)}
                    >
                      {t(c.key)}
                    </a>
                  ))}
                </div>
              )}
            </div>

            <Link to={getPath('/cssbuy-spreadsheet/')} className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap">
              {t('nav.spreadsheet')}
            </Link>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="text-sm font-semibold text-foreground-900 hover:text-primary-600 transition-colors whitespace-nowrap"
            >
              {t('nav.mainSite')}
            </a>

            <div className="relative">
              <button
                onClick={() => setLangOpen(!langOpen)}
                className="flex items-center gap-1.5 px-3 py-1.5 rounded-full border border-background-300 text-sm font-medium text-foreground-700 hover:border-primary-300 hover:text-primary-600 transition-colors whitespace-nowrap"
              >
                <i className="ri-global-line text-sm" /> {currentLang.label} <i className={`ri-arrow-down-s-line transition-transform text-xs ${langOpen ? 'rotate-180' : ''}`} />
              </button>
              {langOpen && (
                <div className="absolute top-full mt-2 right-0 w-40 bg-white rounded-xl border border-background-200 py-2 shadow-lg">
                  {languages.map((l) => (
                    <button
                      key={l.code}
                      onClick={() => {
                        i18n.changeLanguage(l.code);
                        setLangOpen(false);
                      }}
                      className={`w-full text-left px-4 py-2 text-sm transition-colors ${
                        i18n.language === l.code ? 'text-primary-600 bg-primary-50 font-semibold' : 'text-foreground-700 hover:bg-background-50'
                      }`}
                    >
                      {l.label}
                    </button>
                  ))}
                </div>
              )}
            </div>

            <a
              href="https://kaspreadsheet.com"
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex items-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-colors whitespace-nowrap"
            >
              Buy It Now
            </a>
          </div>

          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="lg:hidden p-2 rounded-lg text-foreground-950 hover:bg-background-100 transition-colors"
            aria-label="Menu"
          >
            <i className={`ri-${mobileOpen ? 'close' : 'menu'}-line text-2xl`} />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden bg-white border-t border-background-200 px-4 py-4 shadow-lg max-h-[80vh] overflow-y-auto">
          <Link to={getPath('/')} className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900" onClick={() => setMobileOpen(false)}>
            <i className="ri-home-line text-primary-600" /> {t('nav.home')}
          </Link>
          <Link to={getProductsPath()} className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900 border-t border-background-100" onClick={() => setMobileOpen(false)}>
            <i className="ri-shopping-bag-line text-primary-600" /> {t('nav.products')}
          </Link>
          <Link to={guidePath} className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900 border-t border-background-100" onClick={() => setMobileOpen(false)}>
            <i className="ri-book-open-line text-primary-600" /> {t('nav.shoppingGuide')}
          </Link>

          <button
            onClick={() => setMobileCatOpen(!mobileCatOpen)}
            className="w-full flex items-center justify-between py-3 text-sm font-semibold text-foreground-900 border-t border-background-100"
          >
            <span className="flex items-center gap-2"><i className="ri-grid-line text-primary-600" /> {t('nav.categories')}</span>
            <i className={`ri-arrow-down-s-line transition-transform ${mobileCatOpen ? 'rotate-180' : ''}`} />
          </button>
          {mobileCatOpen && (
            <div className="grid grid-cols-2 gap-1 pb-2">
              {categories.map((c) => (
                <a
                  key={c.key}
                  href={c.url}
                  target="_blank"
                  rel="noopener nofollow"
                  className="flex items-center gap-2 px-3 py-2.5 rounded-lg text-sm text-foreground-700 hover:bg-primary-50 hover:text-primary-700 transition-colors"
                  onClick={() => setMobileOpen(false)}
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-primary-400 flex-shrink-0" />
                  <span className="leading-tight">{t(c.key)}</span>
                </a>
              ))}
            </div>
          )}

          <Link to={getPath('/cssbuy-spreadsheet/')} className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900 border-t border-background-100" onClick={() => setMobileOpen(false)}>
            <i className="ri-table-line text-primary-600" /> {t('nav.spreadsheet')}
          </Link>
          <a href="https://kaspreadsheet.com" target="_blank" rel="noopener nofollow" className="flex items-center gap-2 py-3 text-sm font-semibold text-foreground-900 border-t border-background-100">
            <i className="ri-external-link-line text-primary-600" /> {t('nav.mainSite')}
          </a>

          <div className="flex gap-2 mt-3 pt-3 border-t border-background-100">
            {languages.map((l) => (
              <button
                key={l.code}
                onClick={() => i18n.changeLanguage(l.code)}
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                  i18n.language === l.code ? 'bg-primary-500 text-white border-primary-500' : 'text-foreground-600 border-background-300'
                }`}
              >
                {l.label}
              </button>
            ))}
          </div>

          <a
            href="https://kaspreadsheet.com"
            target="_blank"
            rel="noopener nofollow"
            className="mt-3 w-full inline-flex items-center justify-center gap-1.5 bg-primary-500 hover:bg-primary-600 text-white text-sm font-semibold px-4 py-3 rounded-lg transition-colors"
          >
            Buy It Now
          </a>
        </div>
      )}
    </nav>
  );
}